/**
 * AddDocument Modal component module.
 * @module shared/components/modals/content/AddDocument
 *
 */

import React, {Component} from 'react';
import {connect} from 'react-redux';
import Promise from 'bluebird';
import BaseModal from '../types/Base';
import {_openModal, _removeModal} from '../../../store';
import PropTypes from 'prop-types';
import axiosInstance from '../../../utils/axios';
import {Button, Checkbox, DescriptiveText, Dropdown, FileSelectionButton, Row, TextInput} from '../../../../shared';
import styled from 'styled-components/macro';
import debug from 'debug';
import lazy from 'lazy.js';

const log = debug('circleblack:add-document-modal');

const _UploadCancelRow = styled.div`
  display: flex;
  justify-content: center;
`;

const _NameTypeRow = styled.div`
  display: flex;
  justify-content: center;
`;

/**
 * AddDocument component
 * @name AddDocument
 */
class AddDocument extends Component {
  constructor(props) {
    super(props);
    this.state = {
      title: 'Add Document',
      content: props.content,
      types: [],
      availableTags: [],
      isCertified: {name: 'checkbox', value: false},
      isProcessing: false,
      document: {
        cat: 'Document',
        type: null,
        public: false,
        tags: [],
      },
    };
  }

  updateFile = event => {
    if (!event.target.files.length) {
      return;
    }
    // TODO the share button part of Add Document modal will be implemented later after when the notification reqs are sorted out.
    if (event.target.files && (!event.target.files[0].name.match(/\.pdf$/i) || event.target.files[0].type !== 'application/pdf')) {
      this.props.dispatchOpenModal({
        type: 'MessageModal',
        title: 'Upload Failed',
        body: 'You can only upload articles that are in a PDF file format. Please verify that the article is a PDF and try again.',
      });
      return;
    }
    this.setState({isCertified: {name: 'checkbox', value: false}});
    this.setState({document: {...this.state.document, file: event.target.files[0]}});
  };

  updateType = (name, option) => {
    log('updating type');
    if (!(option.value.toLowerCase() === 'article')) {
      this.setState({isCertified: {name: 'checkbox', value: false}});
    }
    this.setState({type: option});
    this.setState({document: {...this.state.document, type: option.value}});
  };

  updateValue = (name, value) => {
    if (name === 'title') {
      this.setState({document: {...this.state.document, [name]: value}});
    } else {
      this.setState({[name]: value});
    }
  };

  updateTags = (name, value) => {
    this.setState({document: {...this.state.document, tags: [...value.map(val => val.value)]}});
  };

  updateCheckbox = () => {
    this.setState({isCertified: {...this.state.isCertified, value: !this.state.isCertified.value}});
  };

  submitForm = file => {
    const formData = new FormData();
    if (!file) {
      this.props.dispatchOpenModal({
        type: 'MessageModal',
        title: 'Submission Error',
        body: 'Please select a file.',
      });
      return;
    }
    this.setState({document: {...this.state.document, library: true}});
    formData.append('file', file);
    formData.append('data', JSON.stringify(this.state.document));

    if (!!this.props.session.runAsInvestor) {
      this.props.dispatchOpenModal({type: 'MessageModal', title: 'Submission Error', body: 'View as Investor experience does not allow for updating of Investor data.'});
      this.setState({isProcessing: false});
      return;
    }
    this.setState({isProcessing: true});
    axiosInstance.post('/documents', formData)
      .then(() => {
        this.props.dispatchOpenModal({
          type: 'MessageModal',
          title: 'Add Document',
          body: 'Document added successfully.',
        });
        this.props.dispatchRemoveModal(this.props.content);
        this.props.content.refreshPage();
      })
      .catch((err) => {
        this.props.dispatchOpenModal({
          type: 'MessageModal',
          title: 'Submission Error',
          body: err.response && err.response.data && err.response.data.error && err.response.data.error.msg,
        });
      }).finally(() => this.setState({isProcessing: false}));
  };

  componentDidMount() {
    Promise.join(
      axiosInstance.get('/documents/tags').then(res => res.data),
      axiosInstance.get('/documents/types'),
      (tags, types) => {
        log('Retrieving initial data from tags and types');
        let newTypes = [];
        types.data.forEach(type => newTypes.push(type.title));
        this.setState({types: newTypes, availableTags: lazy(tags).uniq().map(tag => ({label: tag, value: tag})).toArray()});
      }
    )
      .catch(err => {
        log('error', err);
        throw err;
      });
  }

  render() {
    const {dispatchRemoveModal} = this.props;
    const {content, title} = this.state;
    let canSubmit = !!(this.state.document.file && this.state.document.title && ([
      'Document',
      'Newsletter',
      'Invoice',
    ].includes(this.state.document.type) || (this.state.document.type === 'Article' && this.state.isCertified.value)));
    return (
      <BaseModal onClose={() => dispatchRemoveModal(content)} title={title} size={'md'}>
        <form name={'addDocumentForm'}
              onSubmit={(event) => {
                event.preventDefault();
                this.submitForm(this.state.document.file);
              }}>
          {this.props.content.description || <DescriptiveText>
            You can upload documents you created outside of CircleBlack to more easily share them with your clients or just keep
            everything in one place. You can only upload documents that are in a PDF file format. Click “Browse” to search for a
            PDF file on your computer.
          </DescriptiveText>}
          <FileSelectionButton allowedTypes={['.pdf']} label='Browse' onChange={this.updateFile}/>
          <_NameTypeRow>
            <TextInput type={'text'}
                       title={'Document Name'}
                       placeholder={'Document Name…'}
                       dataPoint={{name: 'title', value: null}}
                       onChange={this.updateValue}
                       width={'65%'}
                       required
            />
            <Dropdown type={'dropdown'}
                      title={'Document Type'}
                      placeholder={'Document Type…'}
                      dataPoint={{name: 'type', value: this.state.type}}
                      options={this.state.types.length && this.state.types.map(Dropdown.buildSimpleOption) || []}
                      onChange={this.updateType}
                      required={true}
                      width={'35%'}
            />
          </_NameTypeRow>
          {
            this.state.document.type === 'Article'
            && <Checkbox
              title={'I certify that I have the rights to distribute this content (including any uploaded images) to my clients.'}
              dataPoint={this.state.isCertified}
              onChange={this.updateCheckbox}/>
          }
          <DescriptiveText>You can add tags to the document to make it easier to find and sort by. Begin typing to find an existing
            tag, or create a new tag by pressing Enter. </DescriptiveText>
          <Row>
            <Dropdown isMulti={true}
                      type={'dropdown'}
                      placeholder={'Enter Tags…'}
                      dataPoint={{name: 'tags'}}
                      options={this.state.availableTags}
                      isCreatable={true}
                      isClearable={false}
                      onChange={this.updateTags}
                      layout={{grow: 1}}
            />
          </Row>
          <_UploadCancelRow>
            <Button importance={'secondary'}
                    size={'md'}
                    label={'Cancel'}
                    disabled={false}
                    width={'160px'}
                    action={() => dispatchRemoveModal(content)}/>
            <Button importance={'primary'}
                    size={'md'}
                    label={'Upload'}
                    disabled={!canSubmit}
                    width={'160px'}
                    processing={this.state.isProcessing}
                    action={'SUBMIT'}/>
          </_UploadCancelRow>
        </form>
      </BaseModal>
    );
  }
}
const mapStateToProps = state => ({
  session: state.session,
});
const mapDispatchToProps = dispatch => ({
  dispatchRemoveModal: modal => dispatch(_removeModal(modal)),
  dispatchOpenModal: modal => dispatch(_openModal(modal)),
});
export default connect(mapStateToProps, mapDispatchToProps)(AddDocument);

AddDocument.propTypes = {
  /**
   * Session
   */
  session: PropTypes.shape({
    runAsInvestor: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * The remove modal function to dispatches _removeModal from Redux
   */
  dispatchRemoveModal: PropTypes.func.isRequired,
  /**
   * The open modal function to dispatch _openModal from Redux
   */
  dispatchOpenModal: PropTypes.func.isRequired,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    componentName: PropTypes.string.isRequired,
    /**
     * Refresh page with current data. (optional)
     */
    refreshPage: PropTypes.func,
    /**
     * Description, allows to overwrite the default one (when provided)
     */
    description: PropTypes.func,
  }),
};
